import React, { useState, useEffect } from 'react';
import { collection, doc, updateDoc, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    IoArrowBack,
    IoShieldCheckmarkOutline,
    IoSaveOutline,
    IoCashOutline,
    IoCalendarOutline,
    IoCheckmarkCircleOutline
} from 'react-icons/io5';
import './GestionPrestacionesScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function GestionPrestacionesScreen() {
    const { id } = useParams(); // Empleado ID
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const location = useLocation();

    // Contexto SaaS
    const isSaaS = location.pathname.includes('/superuser/hr');
    const basePath = isSaaS ? '/superuser/hr' : '';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [error, setError] = useState('');

    // Sub-registros
    const [empleado, setEmpleado] = useState(null);
    const [prestaciones, setPrestaciones] = useState(null); // Documento de la sub-colección de prestaciones
    const [prestacionId, setPrestacionId] = useState(null); // ID del doc para el update

    // Form fields Prestaciones
    const [tipoPrestacion, setTipoPrestacion] = useState('Ley'); // Ley, Superiores, Honorarios
    const [diasAguinaldo, setDiasAguinaldo] = useState(15);
    const [diasVacaciones, setDiasVacaciones] = useState(12);
    const [primaVacPorcentaje, setPrimaVacPorcentaje] = useState(25);
    const [valesDespensa, setValesDespensa] = useState('');
    const [valesTipo, setValesTipo] = useState('fijo'); // 'fijo' o 'porcentaje'
    const [valesEstrategiaTope, setValesEstrategiaTope] = useState('imss'); // 'imss', 'isr', 'libre'
    const [fondoAhorro, setFondoAhorro] = useState('');
    const [fondoAhorroTopeLey, setFondoAhorroTopeLey] = useState(true); // Porcentaje del fondo retenido/aportado
    const [bonoPuntualidad, setBonoPuntualidad] = useState(0);

    // Form fields Deducciones Fijas
    const [descuentoInfonavit, setDescuentoInfonavit] = useState(0);
    const [descuentoPrestamo, setDescuentoPrestamo] = useState(0);

    useEffect(() => {
        if (!id || !currentUser) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch Empleado Data (for header)
                const empSnap = await getDoc(doc(db, "empleados", id));
                if (empSnap.exists()) {
                    setEmpleado(empSnap.data());
                }

                // Fetch Prestaciones (Assuming 1 doc per employee in sub-collection)
                const prestRef = collection(db, `empleados/${id}/prestaciones`);
                const prestSnap = await getDocs(prestRef);

                if (!prestSnap.empty) {
                    const data = prestSnap.docs[0].data();
                    setPrestacionId(prestSnap.docs[0].id);
                    setPrestaciones(data);

                    setTipoPrestacion(data.tipo || 'Ley');
                    setDiasAguinaldo(data.diasAguinaldo || 15);
                    setDiasVacaciones(data.diasVacaciones || 12);
                    setPrimaVacPorcentaje(data.primaVacacionalPorcentaje || 25);
                    setValesDespensa(data.valesDespensa?.toString() || '');
                    setValesTipo(data.valesTipo || 'fijo');
                    
                    // Retrocompatibilidad con el booleano anterior
                    if (data.valesEstrategiaTope) {
                        setValesEstrategiaTope(data.valesEstrategiaTope);
                    } else if (data.valesTopeLey !== undefined) {
                        setValesEstrategiaTope(data.valesTopeLey ? 'imss' : 'libre');
                    } else {
                        setValesEstrategiaTope('imss');
                    }

                    setFondoAhorro(data.fondoAhorro?.toString() || '');
                    setFondoAhorroTopeLey(data.fondoAhorroTopeLey !== undefined ? data.fondoAhorroTopeLey : true);
                    setBonoPuntualidad(data.bonoPuntualidad || 0);

                    setDescuentoInfonavit(data.descuentoInfonavit || 0);
                    setDescuentoPrestamo(data.descuentoPrestamo || 0);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, currentUser]);

    const enforceLeyDefaults = (tipo) => {
        setTipoPrestacion(tipo);
        if (tipo === 'Ley') {
            setDiasAguinaldo(15);
            setPrimaVacPorcentaje(25);
            setDiasVacaciones(12);
            setValesDespensa(0);
            setFondoAhorro(0);
            setBonoPuntualidad(0);
        } else if (tipo === 'Honorarios') {
            setDiasAguinaldo(0);
            setPrimaVacPorcentaje(0);
            setDiasVacaciones(0);
            setValesDespensa(0);
            setFondoAhorro(0);
            setBonoPuntualidad(0);
        }
    };

    const handleSavePrestaciones = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = {
                tipo: tipoPrestacion,
                diasAguinaldo: Number(diasAguinaldo),
                diasVacaciones: Number(diasVacaciones),
                primaVacacionalPorcentaje: Number(primaVacPorcentaje),
                valesDespensa: valesDespensa ? Number(valesDespensa) : 0,
                valesTipo,
                valesEstrategiaTope,
                fondoAhorro: fondoAhorro ? Number(fondoAhorro) : 0,
                fondoAhorroTopeLey,
                bonoPuntualidad: Number(bonoPuntualidad),
                descuentoInfonavit: Number(descuentoInfonavit),
                descuentoPrestamo: Number(descuentoPrestamo),
                updatedAt: new Date()
            };

            await updateDoc(doc(db, `empleados/${id}/prestaciones`, prestacionId), payload);
            setShowSuccessModal(true);
        } catch (error) {
            console.error("Save error:", error);
            setError("Error al actualizar prestaciones. Revisa la consola.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="prestaciones-loading">Cargando expediente prestacional...</div>;

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/superuser/hr" />
            
            <main className="ks-main">
                <KondoBrandHeader showBack={false} />

                <div className="ks-scroll-area">
                    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
                        
                        {/* Premium Fixed Header Banner - Crimson Gradient */}
                        <div style={{
                            background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                            position: 'relative',
                            overflow: 'hidden',
                            padding: '30px 25px',
                            borderRadius: '24px',
                            marginBottom: '30px',
                            boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', zIndex: 2, position: 'relative' }}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`${basePath}/gestion-empleados`)}
                                    style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        borderRadius: '50%',
                                        width: '44px',
                                        height: '44px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        color: 'white',
                                        flexShrink: 0
                                    }}
                                    className="encuestas-glass-btn"
                                >
                                    <IoArrowBack size={22} />
                                </button>
                                <div>
                                    <h1 style={{ color: 'white', margin: '0 0 6px', fontSize: '1.7rem', fontWeight: '800', fontFamily: "'Manrope', sans-serif" }}>
                                        {isSaaS ? 'Prestaciones Corporativas' : 'Prestaciones de Ley'}
                                    </h1>
                                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.95rem', fontWeight: '500' }}>
                                        {empleado ? `Cálculos fiscales para: ${empleado.nombre} ${empleado.apellidos}` : 'Ajustes Prestacionales'}
                                    </p>
                                </div>
                            </div>
                            <div style={{ position: 'absolute', right: '-20px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.12 }}>
                                <IoShieldCheckmarkOutline size={180} color="white" />
                            </div>
                        </div>

                        <form onSubmit={handleSavePrestaciones} style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                            {error && (
                                <div style={{ color: '#ef4444', backgroundColor: '#fee2e2', padding: '14px', borderRadius: '12px', fontSize: '0.95em', fontWeight: '500', border: '1px solid #fca5a5' }}>
                                    {error}
                                </div>
                            )}

                            <div style={sectionCardStyle}>
                                <h3 style={sectionHeaderStyle}>
                                    <span style={{...iconWrapperStyle, backgroundColor: '#e0f2fe', color: '#0284c7'}}><IoShieldCheckmarkOutline size={18} /></span>
                                    Tipo de Régimen
                                </h3>
                                <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                                    Modificar el régimen alterará de inmediato el cálculo de su Salario Base de Cotización (IMSS) y las cuotas obrero patronales.
                                </p>

                                <div className="regime-selector" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                    <label className={`regime-card ${tipoPrestacion === 'Ley' ? 'regime-active' : ''}`} style={regimeCardStyle(tipoPrestacion === 'Ley')}>
                                        <input type="radio" name="tipo" checked={tipoPrestacion === 'Ley'} onChange={() => enforceLeyDefaults('Ley')} hidden />
                                        <h4 style={{ margin: '0 0 4px 0', fontWeight: '700', fontSize: '1rem' }}>Prestaciones de LFT</h4>
                                        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>SBC dinámico base min</p>
                                    </label>

                                    <label className={`regime-card ${tipoPrestacion === 'Superiores' ? 'regime-active' : ''}`} style={regimeCardStyle(tipoPrestacion === 'Superiores')}>
                                        <input type="radio" name="tipo" checked={tipoPrestacion === 'Superiores'} onChange={() => enforceLeyDefaults('Superiores')} hidden />
                                        <h4 style={{ margin: '0 0 4px 0', fontWeight: '700', fontSize: '1rem' }}>Superiores a la Ley</h4>
                                        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>Mayor Integración IMSS</p>
                                    </label>

                                    <label className={`regime-card ${tipoPrestacion === 'Honorarios' ? 'regime-active' : ''}`} style={regimeCardStyle(tipoPrestacion === 'Honorarios')}>
                                        <input type="radio" name="tipo" checked={tipoPrestacion === 'Honorarios'} onChange={() => enforceLeyDefaults('Honorarios')} hidden />
                                        <h4 style={{ margin: '0 0 4px 0', fontWeight: '700', fontSize: '1rem' }}>Honorarios</h4>
                                        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>Sin obligaciones LFT/IMSS</p>
                                    </label>
                                </div>
                            </div>

                            {tipoPrestacion !== 'Honorarios' && (
                                <div style={sectionCardStyle}>
                                    <h3 style={sectionHeaderStyle}>
                                        <span style={{...iconWrapperStyle, backgroundColor: '#f3e8ff', color: '#7e22ce'}}><IoCalendarOutline size={18} /></span>
                                        Configuración Factor Integración
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                                        <div>
                                            <label style={labelStyle}>Días de Aguinaldo Anual</label>
                                            <input
                                                type="number"
                                                value={diasAguinaldo}
                                                onChange={e => setDiasAguinaldo(e.target.value)}
                                                min="15"
                                                disabled={tipoPrestacion === 'Ley'}
                                                style={inputStyle}
                                            />
                                            {tipoPrestacion === 'Ley' && <small style={{ color: '#64748b', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>*Mínimo por Ley: 15</small>}
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Días de Vacaciones (Periodo actual)</label>
                                            <input
                                                type="number"
                                                value={diasVacaciones}
                                                onChange={e => setDiasVacaciones(e.target.value)}
                                                min="12"
                                                disabled={tipoPrestacion === 'Ley'}
                                                style={inputStyle}
                                            />
                                            {tipoPrestacion === 'Ley' && <small style={{ color: '#64748b', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>*Mínimo Ley VD: 12</small>}
                                        </div>
                                        <div>
                                            <label style={labelStyle}>% Prima Vacacional</label>
                                            <input
                                                type="number"
                                                value={primaVacPorcentaje}
                                                onChange={e => setPrimaVacPorcentaje(e.target.value)}
                                                min="25"
                                                disabled={tipoPrestacion === 'Ley'}
                                                style={inputStyle}
                                            />
                                            {tipoPrestacion === 'Ley' && <small style={{ color: '#64748b', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>*Mínimo LFT: 25%</small>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {tipoPrestacion === 'Superiores' && (
                                <div style={{...sectionCardStyle, background: '#faf5ff', border: '1px solid #e9d5ff'}}>
                                    <h3 style={sectionHeaderStyle}>
                                        <span style={{...iconWrapperStyle, backgroundColor: '#f3e8ff', color: '#7e22ce'}}><IoCashOutline size={18} /></span>
                                        Retribuciones Extras y Exentos (Superiores)
                                    </h3>
                                    <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                                        Estos montos pueden tener impacto en impuestos retenidos (ISR) si superan los límites de exención (UMAs).
                                    </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 12px 0', color: '#0f172a', fontSize: '1rem', fontWeight: '700' }}>Vales de Despensa</h4>
                                            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}>
                                                    <input 
                                                        type="radio" 
                                                        name="valesTipo" 
                                                        value="fijo" 
                                                        checked={valesTipo === 'fijo'} 
                                                        onChange={() => setValesTipo('fijo')} 
                                                    />
                                                    Monto Fijo
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}>
                                                    <input 
                                                        type="radio" 
                                                        name="valesTipo" 
                                                        value="porcentaje" 
                                                        checked={valesTipo === 'porcentaje'} 
                                                        onChange={() => setValesTipo('porcentaje')} 
                                                    />
                                                    Porcentaje (%)
                                                </label>
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-start' }}>
                                                <div style={{ flex: '1 1 250px' }}>
                                                    <input
                                                        type="number"
                                                        value={valesDespensa}
                                                        onChange={e => setValesDespensa(e.target.value)}
                                                        min="0" step="0.01"
                                                        placeholder={valesTipo === 'fijo' ? "Ej: 1500" : "Ej: 10"}
                                                        style={inputStyle}
                                                    />
                                                </div>
                                                <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: valesEstrategiaTope === 'imss' ? '#dcfce7' : '#f8fafc', border: '1px solid ' + (valesEstrategiaTope === 'imss' ? '#86efac' : '#e2e8f0'), borderRadius: '10px', fontSize: '0.8rem', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="valesEstrategia"
                                                            value="imss"
                                                            checked={valesEstrategiaTope === 'imss'}
                                                            onChange={() => setValesEstrategiaTope('imss')}
                                                        />
                                                        Topar a IMSS (Máx 40% UMA diario)
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: valesEstrategiaTope === 'isr' ? '#fef08a' : '#f8fafc', border: '1px solid ' + (valesEstrategiaTope === 'isr' ? '#fde047' : '#e2e8f0'), borderRadius: '10px', fontSize: '0.8rem', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="valesEstrategia"
                                                            value="isr"
                                                            checked={valesEstrategiaTope === 'isr'}
                                                            onChange={() => setValesEstrategiaTope('isr')}
                                                        />
                                                        Topar a ISR (Máx 1 UMA mens. / Integra SBC)
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: valesEstrategiaTope === 'libre' ? '#fee2e2' : '#f8fafc', border: '1px solid ' + (valesEstrategiaTope === 'libre' ? '#fca5a5' : '#e2e8f0'), borderRadius: '10px', fontSize: '0.8rem', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="valesEstrategia"
                                                            value="libre"
                                                            checked={valesEstrategiaTope === 'libre'}
                                                            onChange={() => setValesEstrategiaTope('libre')}
                                                        />
                                                        Libre (Grava e Integra todo excedente)
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 12px 0', color: '#0f172a', fontSize: '1rem', fontWeight: '700' }}>Fondo de Ahorro</h4>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
                                                <div style={{ flex: '1 1 250px' }}>
                                                    <label style={labelStyle}>Retención / Aportación (%)</label>
                                                    <input
                                                        type="number"
                                                        value={fondoAhorro}
                                                        onChange={e => setFondoAhorro(e.target.value)}
                                                        min="0" max={fondoAhorroTopeLey ? "13" : "100"} step="0.1"
                                                        placeholder="Ej: 13"
                                                        style={inputStyle}
                                                    />
                                                </div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 250px', padding: '12px 16px', background: fondoAhorroTopeLey ? '#dcfce7' : '#fee2e2', border: '1px solid ' + (fondoAhorroTopeLey ? '#86efac' : '#fca5a5'), borderRadius: '12px', fontSize: '0.85rem', cursor: 'pointer', boxSizing: 'border-box' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={fondoAhorroTopeLey}
                                                        onChange={e => setFondoAhorroTopeLey(e.target.checked)}
                                                        style={{ width: '18px', height: '18px' }}
                                                    />
                                                    {fondoAhorroTopeLey ? 'Protegido (Tope máx 13% LISR)' : 'Libre (Excedentes Gravarán ISR)'}
                                                </label>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label style={labelStyle}>Bono de Puntualidad / Asistencia Max. $</label>
                                            <input
                                                type="number"
                                                value={bonoPuntualidad}
                                                onChange={e => setBonoPuntualidad(e.target.value)}
                                                min="0" step="0.01"
                                                style={inputStyle}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{...sectionCardStyle, background: '#fff1f2', border: '1px solid #fecdd3'}}>
                                <h3 style={{...sectionHeaderStyle, color: '#e11d48'}}>
                                    <span style={{...iconWrapperStyle, backgroundColor: '#ffe4e6', color: '#e11d48'}}><IoCashOutline size={18} /></span>
                                    Retenciones Inst. y Préstamos
                                </h3>
                                <p style={{ margin: '0 0 20px', fontSize: '0.9rem', color: '#be123c', lineHeight: '1.5' }}>
                                    Estos montos se descontarán automáticamente de sus percepciones netas cada que se corra el cálculo de la nómina del periodo.
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                                    <div>
                                        <label style={{...labelStyle, color: '#9f1239'}}>Descuento INFONAVIT / FONACOT (Periodo) $</label>
                                        <input
                                            type="number"
                                            value={descuentoInfonavit}
                                            onChange={e => setDescuentoInfonavit(e.target.value)}
                                            min="0" step="0.01"
                                            style={{...inputStyle, backgroundColor: '#fff8f8', borderColor: '#fca5a5'}}
                                        />
                                        <small style={{ color: '#e11d48', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>*Monto fijo a descontar (V.S.M / Porcentaje / Cuota)</small>
                                    </div>
                                    <div>
                                        <label style={{...labelStyle, color: '#9f1239'}}>Préstamo Fraccionamiento (Periodo) $</label>
                                        <input
                                            type="number"
                                            value={descuentoPrestamo}
                                            onChange={e => setDescuentoPrestamo(e.target.value)}
                                            min="0" step="0.01"
                                            style={{...inputStyle, backgroundColor: '#fff8f8', borderColor: '#fca5a5'}}
                                        />
                                        <small style={{ color: '#e11d48', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>*Abono al capital adeudado en la empresa.</small>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                <button 
                                    type="submit" 
                                    disabled={saving} 
                                    style={{ 
                                        background: saving ? '#93c5fd' : '#3b82f6', 
                                        color: 'white', 
                                        border: 'none', 
                                        padding: '14px 32px', 
                                        borderRadius: '14px', 
                                        fontSize: '1rem', 
                                        fontWeight: '700', 
                                        cursor: saving ? 'not-allowed' : 'pointer', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '10px',
                                        boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <IoSaveOutline size={20} />
                                    {saving ? 'Aplicando Motor F.I...' : 'Guardar Configuración de Empleado'}
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </main>

            {/* Custom Success Modal */}
            {showSuccessModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(6px)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 99999
                }}>
                    <div style={{
                        background: 'white', padding: '35px 30px', borderRadius: '28px',
                        width: '90%', maxWidth: '400px', textAlign: 'center',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        fontFamily: "'Inter', sans-serif"
                    }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '50%',
                            backgroundColor: '#dcfce7', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px', color: '#16a34a',
                            boxShadow: '0 8px 16px rgba(22, 163, 74, 0.15)'
                        }}>
                            <IoCheckmarkCircleOutline size={36} />
                        </div>
                        <h3 style={{ margin: '0 0 10px', fontSize: '1.4rem', fontWeight: '800', color: '#0f172a' }}>
                            Configuración Guardada
                        </h3>
                        <p style={{ margin: '0 0 24px', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
                            Prestaciones actualizadas correctamente. El Factor de Integración (SBC) se calculará en la próxima nómina.
                        </p>
                        <button
                            onClick={() => {
                                setShowSuccessModal(false);
                                navigate(`${basePath}/gestion-empleados`);
                            }}
                            style={{
                                width: '100%', padding: '14px 24px', border: 'none',
                                borderRadius: '14px', background: '#3b82f6', color: 'white',
                                fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.25)',
                                transition: 'all 0.2s'
                            }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}

            <KondoBottomNav activeRoute="/superuser/hr" />
        </div>
    );
}

// Internal styles matching the premium look
const sectionCardStyle = { 
    background: 'white', 
    padding: '28px', 
    borderRadius: '24px', 
    border: '1px solid #f1f5f9', 
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)' 
};

const sectionHeaderStyle = { 
    margin: '0 0 24px', 
    display: 'flex', 
    alignItems: 'center', 
    gap: '12px', 
    color: '#0f172a',
    fontSize: '1.15rem',
    fontWeight: '800',
    fontFamily: "'Manrope', sans-serif"
};

const iconWrapperStyle = {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const labelStyle = { 
    display: 'block', 
    fontSize: '0.85rem', 
    color: '#475569', 
    marginBottom: '8px', 
    fontWeight: '600' 
};

const inputStyle = { 
    width: '100%', 
    padding: '12px 16px', 
    border: '1px solid #cbd5e1', 
    borderRadius: '12px', 
    fontSize: '0.95rem', 
    color: '#1e293b', 
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    outline: 'none',
    backgroundColor: '#fafafa'
};

const selectStyle = { 
    ...inputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='%2364748b' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '40px'
};

const regimeCardStyle = (isActive) => ({
    background: isActive ? '#f0fdf4' : '#ffffff',
    border: '2px solid ' + (isActive ? '#22c55e' : '#cbd5e1'),
    borderRadius: '16px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: isActive ? '0 4px 12px rgba(34, 197, 94, 0.12)' : 'none',
    display: 'block'
});

export default GestionPrestacionesScreen;
